import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { setUserInfo } from "../../../../store/auth/authSlice";
import { useGetUserQuery } from "../../../../store/auth/usersApiSlice";
import { useGetCompanyQuery } from "../../../../store/Company/companyApiSlice";
import { buildWebProfileSummary } from "../../../../constants/webProfilePricing";
import { CommonToast } from "../../../common/Toast";
import {
  isReportRequestFrom,
  modeCopy,
  requestedCompanyNameFor,
  resolveCompanyId,
  resolveMode,
  showsUpsell,
} from "./paymentDetailsFields";

/* Everything the u1 payment page needs, transcribed from PaymentDetails.jsx. */
export const usePaymentDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { token } = useSelector((state) => state.auth);
  const { data, isSuccess, isError, error } = useGetUserQuery(token);

  const state = location.state;
  const { planDetails } = state || {};

  const companyId = resolveCompanyId(state);
  const isReportRequest = isReportRequestFrom(state);
  const requestedCompanyName = requestedCompanyNameFor(isReportRequest);

  const mode = resolveMode(planDetails);
  const membershipPlan = planDetails?.plan;

  /* Ex-VAT (TIP-646). Tax is Stripe's to compute from the billing address, so
     the summary carries no VAT figure until create_payment answers with one;
     the page overlays it with applyQuotedTax. */
  const summary = buildWebProfileSummary({ mode, plan: membershipPlan });

  const { data: company } = useGetCompanyQuery(
    { token, companyId },
    { skip: !companyId }
  );

  useEffect(() => {
    if (isSuccess) {
      dispatch(setUserInfo({ ...data }));
      /* Transcribed: arriving at a payable step ends whatever navigation was
         being tracked, so the keys that drive "back to where you were" go. */
      localStorage.removeItem("slug");
      localStorage.removeItem("lastVisited");
    }
  }, [data, dispatch, isSuccess, isError, error]);

  /* TIP-530 — never render a payable step without a target company.
     TIP-556 — report orders are exempt: their company does not exist yet. */
  useEffect(() => {
    if (!companyId && !isReportRequest) {
      CommonToast.error("Select a business to buy its Web Profile.");
      navigate("/search", { replace: true });
    }
  }, [companyId, isReportRequest, navigate]);

  /* TIP-564 — a stale report-order flag must not leak into this purchase's
     meta_data. Guarded on isReportRequest so it never fires while an actual
     report order is in progress, including TIP-556's mid-flow redirect. */
  useEffect(() => {
    if (!isReportRequest) {
      localStorage.removeItem("createWorldboxCompany");
      localStorage.removeItem("companyDescription");
    }
  }, [isReportRequest]);

  return {
    blocked: !companyId && !isReportRequest,
    companyId,
    company,
    isReportRequest,
    requestedCompanyName,
    planDetails,
    mode,
    summary,
    copy: modeCopy(mode),
    upsell: showsUpsell(mode),
    /* The upsell's own action, transcribed: back to the plans step, carrying
       the company so that step keeps its target. */
    switchToMembership: () => navigate("/plans", { state: { company_id: companyId } }),
    goBack: () => navigate(-1),
  };
};

export default usePaymentDetails;
